




























































import {
  defineComponent,
  ref,
  watch,
} from '@vue/composition-api';
import type { CouponRolePo } from '@/api/coupon/types';
import { Popup } from '@hb/coffee';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { CloseOutline } from '@hb/coffee-icons';
import { TP_QUICK_WITHDRAW_CARD_COUPON } from '../../tianqi';
import CouponCard from '../coupon-card/index.vue';

// static component
// [功能说明]: 优惠券选择弹窗组件
interface Props {
  visible?: boolean;
  availableCouponList?: CouponRolePo[];
  unavailableCouponList?: CouponRolePo[];
  couponCancelDisable?: boolean;
  selectedCoupon?: CouponRolePo | null;
  containerHeight?: number;
}

export default defineComponent({
  name: 'CouponSelectModal',
  components: {
    Popup,
    CouponCard,
    CloseOutline,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    availableCouponList: {
      type: Array as () => CouponRolePo[],
      default: () => [],
    },
    unavailableCouponList: {
      type: Array as () => CouponRolePo[],
      default: () => [],
    },
    couponCancelDisable: {
      type: Boolean,
      default: false,
    },
    selectedCoupon: {
      type: Object as () => CouponRolePo | null,
      default: null,
    },
    containerHeight: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close', 'select', 'no-coupon'],
  setup(props: Props, { emit }) {
    const selectedType = ref<'none' | 'coupon'>('none');
    const selectedIndex = ref<number>(-1);
    const selectedAvailable = ref<boolean>(true);

    // 根据外部 selectedCoupon 更新内部状态
    const updateInternalState = (coupon: CouponRolePo | null) => {
      if (!coupon) {
        selectedType.value = 'none';
        selectedIndex.value = -1;
        selectedAvailable.value = true;
        return;
      }

      // 在可用优惠券列表中查找
      const availableIndex = props.availableCouponList?.findIndex(
        (item) => item.couponId === coupon.couponId
      );
      if (availableIndex !== undefined && availableIndex >= 0) {
        selectedType.value = 'coupon';
        selectedIndex.value = availableIndex;
        selectedAvailable.value = true;
        return;
      }

      // 在不可用优惠券列表中查找
      const unavailableIndex = props.unavailableCouponList?.findIndex(
        (item) => item.couponId === coupon.couponId
      );
      if (unavailableIndex !== undefined && unavailableIndex >= 0) {
        selectedType.value = 'coupon';
        selectedIndex.value = unavailableIndex;
        selectedAvailable.value = false;
        return;
      }

      // 如果都没找到，重置为未选择状态
      selectedType.value = 'none';
      selectedIndex.value = -1;
      selectedAvailable.value = true;
    };

    // 监听 selectedCoupon 变化
    watch(
      () => props.selectedCoupon,
      (newCoupon) => {
        updateInternalState(newCoupon || null);
      },
      { immediate: true },
    );

    // 监听弹窗显示状态，触发曝光埋点
    watch(
      () => props.visible,
      (isVisible) => {
        if (isVisible) {
          ubtSetupModuleExpose(TP_QUICK_WITHDRAW_CARD_COUPON);
        }
      },
    );

    // 处理关闭弹窗
    const handleClose = () => {
      emit('close');
    };

    // 处理不选择优惠券
    const handleNoCoupon = () => {
      selectedType.value = 'none';
      selectedIndex.value = -1;
      selectedAvailable.value = true;
      emit('no-coupon');
    };

    // 处理选择优惠券
    const handleSelectCoupon = (coupon: CouponRolePo, index: number) => {
      ubtSetupPageButton(TP_QUICK_WITHDRAW_CARD_COUPON, {
        couponId: coupon.couponId,
      });
      selectedType.value = 'coupon';
      selectedIndex.value = index;
      selectedAvailable.value = true;
      emit('select', coupon, index, true);
    };

    return {
      selectedType,
      selectedIndex,
      selectedAvailable,
      handleClose,
      handleNoCoupon,
      handleSelectCoupon,
      containerHeight: props.containerHeight,
    };
  },
});
